import { initShaderProgram, IShaderProgramInfo } from "../Shader";
import { Camera } from "../Camera";
import { mat4 } from "gl-matrix";
import { IRGBA } from "../TimelineUtils";

interface IGLAttributes {
  position: WebGLBuffer;
}

export interface IOutlineBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export class OutlinesGL {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;

    uniform mat4 uModelViewMatrix;
    uniform mat4 uProjectionMatrix;
    uniform vec4 uColor;

    varying lowp vec4 vColor;

    void main(void) {
      gl_Position = uProjectionMatrix * uModelViewMatrix * aVertexPosition;
      vColor = uColor;
    }
  `;
  // Fragment shader program
  private readonly fsSource = `
    varying lowp vec4 vColor;
    void main(void) {
      gl_FragColor = vColor;

    }
  `;

  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;
  private buffers: IGLAttributes;
  private shadingBuffers: IGLAttributes;
  private positions: number[];
  private shadingPositions: number[];

  constructor(gl: WebGLRenderingContext) {
    // Initialize a shader program; this is where all the lighting
    // for the vertices and so forth is established.
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);

    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };

    // Now create an array of positions for the square.
    this.positions = [];

    // Here's where we call the routine that builds all the
    // objects we'll be drawing.
    this.buffers = this.initBuffers(gl, this.positions);

    // Now create an array of positions for the square.
    this.shadingPositions = [];
    this.shadingBuffers = this.initBuffers(gl, this.shadingPositions);
  }

  //
  // initBuffers
  //
  // Initialize the buffers we'll need. For this demo, we just
  // have one object -- a simple two-dimensional square.
  //
  private initBuffers = (gl: WebGLRenderingContext, positions: number[]): IGLAttributes => {
    // Create a buffer for the square's positions.
    const positionBuffer = gl.createBuffer();

    // Select the positionBuffer as the one to apply buffer
    // operations to from here out.
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

    // Now pass the list of positions into WebGL to build the
    // shape. We do this by creating a Float32Array from the
    // JavaScript array, then use it to fill the current buffer.

    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.DYNAMIC_DRAW);

    return {
      position: positionBuffer,
    };
  };

  public updateBuffers = (gl: WebGLRenderingContext, outlines: IOutlineBox[]): void => {
    this.positions = [];
    this.shadingPositions = [];
    for (const outline of outlines) {
      const p0 = [outline.x0, outline.y0];
      const p1 = [outline.x0, outline.y1];
      const p2 = [outline.x1, outline.y1];
      const p3 = [outline.x1, outline.y0];
      this.positions.push(...p0, ...p1, ...p1, ...p2, ...p2, ...p3, ...p3, ...p0);
      this.shadingPositions.push(...p0, ...p1, ...p3, ...p1, ...p3, ...p2 /*, ...p3, ...p2, ...p1*/);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers.position);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.positions), gl.DYNAMIC_DRAW);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.shadingBuffers.position);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(this.shadingPositions), gl.DYNAMIC_DRAW);
    // console.log("BOO:", this.buffers.position, this.shadingBuffers.position);
  };

  public draw = (gl: WebGLRenderingContext, camera: Camera, color: IRGBA, z: number, drawShading = false): void => {
    if (this.positions.length < 4) {
      return;
    }
    // Set the drawing position to the "identity" point, which is
    // the center of the scene.
    const modelViewMatrix = mat4.create();

    // Now move the drawing position a bit to where we want to
    // start drawing the square.

    mat4.translate(
      modelViewMatrix, // destination matrix
      modelViewMatrix, // matrix to translate
      [0.0, 0.0, z]
    ); // amount to translate

    // Tell WebGL to use our program when drawing
    gl.useProgram(this.programInfo.program);

    // this.drawQuad()

    // Set the shader uniforms
    const projectionMatrix = camera.getProjectionMatrix(gl);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, modelViewMatrix);

    this.drawQuad(gl, this.buffers.position, this.positions.length / 2, color, gl.LINES);
    if (drawShading) {
      this.drawQuad(
        gl,
        this.shadingBuffers.position,
        this.shadingPositions.length / 2,
        { red: 0.1, green: 0.1, blue: 0.1, alpha: 0.9 },
        gl.TRIANGLES
      );
    }
  };

  private drawQuad = (
    gl: WebGLRenderingContext,
    buffer: WebGLBuffer,
    numDraw: number,
    color: IRGBA,
    mode: number
  ): void => {
    gl.uniform4f(this.programInfo.uniformLocations.color, color.red, color.green, color.blue, color.alpha);

    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    {
      const numComponents = 2;
      const type = gl.FLOAT;
      const normalize = false;
      const stride = 0;
      const offset = 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

      gl.vertexAttribPointer(
        this.programInfo.attribLocations.vertexPosition,
        numComponents,
        type,
        normalize,
        stride,
        offset
      );
      gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
    }

    {
      const offset = 0;
      gl.drawArrays(mode, offset, numDraw);
    }
  };
}
